import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Generic "remove-from-list with animation" state machine.
 *
 * A caller starts an exit for a given item. The hook walks the item through
 * three phases (`animating` → `hold` → `collapse`) on setTimeout chains and
 * then calls `onComplete`. While an item is exiting it stays visible in the
 * `displayItems` output — either as a "live" item (still in `liveItems`) or
 * as a "ghost" (removed from the live list but still animating out).
 *
 * The hook owns all race-safety (exit sequence counters) and cleanup on
 * unmount. Timing values are provided by the caller.
 */

export type ExitPhase = 'animating' | 'hold' | 'collapse';

export type ExitEntry<T, Meta> = {
  /** Snapshot of the item captured when the exit started. */
  snapshot: T;
  meta: Meta;
  /** Monotonic sequence, used to discard stale state updates. */
  seq: number;
  phase: ExitPhase;
};

export type ExitDisplayItem<T, Meta> =
  | {
      kind: 'live';
      item: T;
      id: string;
      index: number;
      exit: ExitEntry<T, Meta> | null;
    }
  | {
      kind: 'ghost';
      item: T;
      id: string;
      exit: ExitEntry<T, Meta>;
    };

export type ExitTimings = {
  animate: number;
  hold: number;
  collapse: number;
};

type Options<T, Meta> = {
  getId: (item: T) => string;
  liveItems: T[];
  timings: ExitTimings;
  onComplete?: (id: string, meta: Meta) => void;
};

export function useListItemExitAnimation<T, Meta = void>({
  getId,
  liveItems,
  timings,
  onComplete,
}: Options<T, Meta>) {
  const [exitingById, setExitingById] = useState<
    Record<string, ExitEntry<T, Meta>>
  >({});
  const seqRef = useRef(0);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>[]>>(
    new Map()
  );
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const clearTimeoutsFor = useCallback((id: string) => {
    const ts = timeoutsRef.current.get(id);
    if (ts) {
      for (const t of ts) clearTimeout(t);
      timeoutsRef.current.delete(id);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      for (const ts of timeoutsRef.current.values()) {
        for (const t of ts) clearTimeout(t);
      }
      timeoutsRef.current.clear();
    };
  }, []);

  const startExit = useCallback(
    (item: T, meta: Meta) => {
      const id = getId(item);
      seqRef.current += 1;
      const seq = seqRef.current;

      // Reset any in-flight exit for the same id
      clearTimeoutsFor(id);

      setExitingById(prev => ({
        ...prev,
        [id]: { snapshot: item, meta, seq, phase: 'animating' },
      }));

      const advanceTo = (phase: ExitPhase) => {
        setExitingById(prev => {
          const cur = prev[id];
          if (!cur || cur.seq !== seq) return prev;
          return { ...prev, [id]: { ...cur, phase } };
        });
      };

      const finish = () => {
        clearTimeoutsFor(id);
        setExitingById(prev => {
          const cur = prev[id];
          if (!cur || cur.seq !== seq) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
        onCompleteRef.current?.(id, meta);
      };

      const tHold = setTimeout(() => advanceTo('hold'), timings.animate);
      const tCollapse = setTimeout(
        () => advanceTo('collapse'),
        timings.animate + timings.hold
      );
      const tDone = setTimeout(
        finish,
        timings.animate + timings.hold + timings.collapse
      );

      timeoutsRef.current.set(id, [tHold, tCollapse, tDone]);

      const cancel = () => {
        clearTimeoutsFor(id);
        setExitingById(prev => {
          const cur = prev[id];
          if (!cur || cur.seq !== seq) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      };

      return { seq, cancel };
    },
    [getId, clearTimeoutsFor, timings.animate, timings.hold, timings.collapse]
  );

  const displayItems = useMemo((): ExitDisplayItem<T, Meta>[] => {
    const liveIds = new Set(liveItems.map(getId));

    const live: ExitDisplayItem<T, Meta>[] = liveItems.map((item, index) => {
      const id = getId(item);
      const exit = exitingById[id] ?? null;
      return { kind: 'live', item, id, index, exit };
    });

    const ghosts: ExitDisplayItem<T, Meta>[] = [];
    for (const id of Object.keys(exitingById)) {
      if (liveIds.has(id)) continue;
      const entry = exitingById[id];
      if (!entry) continue;
      ghosts.push({ kind: 'ghost', item: entry.snapshot, id, exit: entry });
    }

    return [...live, ...ghosts];
  }, [liveItems, exitingById, getId]);

  const getExit = useCallback(
    (id: string): ExitEntry<T, Meta> | null => exitingById[id] ?? null,
    [exitingById]
  );

  return { displayItems, startExit, getExit };
}
